import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 py-16 max-w-4xl">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Refund Policy</h1>
        <p className="text-gray-600 mb-8">Last updated: January 2025</p>

        <div className="bg-white rounded-lg shadow-sm p-8 space-y-6">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Overview</h2>
            <p className="text-gray-700 leading-relaxed">
              We want you to be completely satisfied with our WhatsApp Business API services. This Refund Policy outlines the circumstances under which refunds may be issued and the process for requesting a refund.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Subscription Refunds</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Monthly Subscriptions</h3>
                <p className="text-gray-700 leading-relaxed">
                  Monthly subscription fees are non-refundable. If you cancel your monthly subscription, you will continue to have access to the service until the end of your current billing period.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Annual Subscriptions</h3>
                <p className="text-gray-700 leading-relaxed">
                  Annual subscriptions may be eligible for a pro-rata refund if cancelled within the first 30 days of purchase. After 30 days, annual subscriptions are non-refundable. You will continue to have access to the service until the end of your annual period.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. WhatsApp Message Credits</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              WhatsApp message credits (conversation charges) are governed by the following refund terms:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Unused message credits are non-refundable</li>
              <li>Credits do not expire as long as your account remains active</li>
              <li>If your account is terminated, any remaining credits will be forfeited</li>
              <li>Failed message delivery due to technical issues on our end will be credited back to your account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Service Interruption</h2>
            <p className="text-gray-700 leading-relaxed">
              If our service experiences an interruption that:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-2">
              <li>Lasts more than 24 consecutive hours</li>
              <li>Is caused by factors within our control</li>
              <li>Significantly impacts your ability to use the service</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              You may be eligible for a service credit equal to the pro-rated cost of the downtime period. Service credits must be requested within 7 days of the incident.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. 30-Day Money-Back Guarantee</h2>
            <p className="text-gray-700 leading-relaxed">
              We offer a 30-day money-back guarantee for first-time annual plan subscribers. If you're not satisfied with our service within the first 30 days of your annual subscription, you can request a full refund. This guarantee applies only to the subscription fee and does not include WhatsApp message credits already consumed.
            </p>
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>Note:</strong> The money-back guarantee is available only once per customer and does not apply to monthly subscriptions or renewals.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. How to Request a Refund</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To request a refund, please follow these steps:
            </p>
            <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
              <li>Contact our support team via email at support@whatsappbusiness.com</li>
              <li>Include your account details and reason for the refund request</li>
              <li>Provide any relevant documentation or screenshots if applicable</li>
              <li>Allow 5-7 business days for review of your request</li>
            </ol>
            <p className="text-gray-700 leading-relaxed mt-4">
              If your refund is approved, it will be processed within 10 business days and credited to your original payment method.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Non-Refundable Items</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The following items are non-refundable:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Setup fees or onboarding charges</li>
              <li>WhatsApp Business API procurement fees (these are free anyway)</li>
              <li>Custom development or integration work</li>
              <li>Training or consulting services</li>
              <li>Third-party service charges</li>
              <li>Add-on features purchased separately</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Chargebacks</h2>
            <p className="text-gray-700 leading-relaxed">
              If you file a chargeback with your bank or credit card company without first contacting us to resolve the issue, your account will be immediately suspended, and you may be liable for chargeback fees. We encourage you to contact our support team to resolve any billing disputes before initiating a chargeback.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Exceptions</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to make exceptions to this refund policy on a case-by-case basis. Such exceptions will be made at our sole discretion and do not set a precedent for future refund requests.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed">
              If you have any questions about our Refund Policy, please contact us:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-700">Email: support@whatsappbusiness.com</p>
              <p className="text-gray-700">Phone: +91 9866461437</p>
              <p className="text-gray-700">Hours: Monday-Friday, 9:00 AM - 6:00 PM IST</p>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RefundPolicy;